import React, {
	createContext,
	ReactNode,
	useContext,
	useState,
	useMemo,
} from 'react';
import { useQuery, useQueryClient } from 'react-query';
import {
	CargoContractSides,
	Currencies,
	FixtureTypes,
} from '@shared/utils/constants';
import { Values } from '@shared/utils/objectEnums';
import { MakeFieldsNullable } from '@shared/utils/generics';
import type { GetVoyageDetailsResponse } from '@api/features/voyages/getVoyageDetails';
import type { GetFixtureDetailsResponse } from '@api/features/fixtures/getFixtureDetails';
import type { TcFixtureProps } from '@api/models/tc-fixture';
import type { SpotFixtureProps } from '@api/models/spot-fixture';
import type { BbFixtureProps } from '@api/models/bb-fixture';
import type { GetVoyagesResponse } from '@api/features/voyages/getVoyages';
import type { GetVoyageCompletionTypeResponse } from '@api/features/voyages/getVoyageCompletionType';
import type { GetVoyageInvoicesResponse } from '@api/features/invoices/getVoyageInvoices';
import type { GetCargosAndLaytimesResponse } from '@api/features/laytime/getCargosAndLaytimes';
import type { GetVoyageExpensesResponse } from '@api/features/invoices/getVoyageExpenses';
import type { GetVesselDetailsResponse } from '@api/features/vessels/getVesselDetails';
import type { GetEstimateDetailsResponse } from '@api/features/estimates/getEstimateDetails';
import type { GetDisbursementAccountsResponse } from '@api/features/disbursement-accounts/getDisbursementAccounts';
import type { TcPnl } from '@api/utils/pnl/tc/calculateTcPnl';
import { VcPnl } from '@api/utils/pnl/calculateVcPnl';
import {
	getCargosAndLaytimes,
	getDisbursementAccounts,
	getEstimateDetails,
	getFixtureDetails,
	getTcPnl,
	getVcPnl,
	getVesselDetails,
	getVoyageCompletionType,
	getVoyageDetails,
	getVoyageExpenses,
	getVoyageInvoices,
	getVoyages,
	syncVoyageToTheShip,
	updateVoyage,
} from '@client/lib/api';
import showSuccessNotification from '@client/utils/showSuccessNotification';
import showErrorNotification from '@client/utils/showErrorNotification';
import ContractLoading from './components/ContractLoading';

type VoyageContextType = {
	voyageId: number;
	voyageDetails: GetVoyageDetailsResponse;
	voyageDetailsLoading: boolean;
	refreshVoyageDetails: () => void;
	fixtureCurrency: Values<typeof Currencies>;
	tcFixtureDetails: GetFixtureDetailsResponse<TcFixtureProps | BbFixtureProps>;
	vcFixtureDetails: GetFixtureDetailsResponse<SpotFixtureProps>;
	fixtureDetails: GetFixtureDetailsResponse<TcFixtureProps | BbFixtureProps | SpotFixtureProps>;
	fixtureDetailsLoading: boolean;
	refreshFixtureDetails: () => void;
	refreshDetails: () => void;
	allVoyages: GetVoyagesResponse['rows'] | undefined;
	updateVoyageField: (field: string, value: any, contractId?: number | null) => void;
	voyageCompletion: GetVoyageCompletionTypeResponse | undefined;
	voyageInvoices: GetVoyageInvoicesResponse | undefined;
	voyageInvoicesLoading: boolean;
	refreshVoyageInvoices: () => void;
	cargos: GetCargosAndLaytimesResponse['cargosAndLaytimes'] | undefined;
	transformedCargos: GetCargosAndLaytimesResponse['cargos'] | undefined;
	cargosLoading: boolean;
	refreshCargos: () => void;
	vendorExpenses: GetVoyageExpensesResponse | undefined;
	vendorExpensesLoading: boolean;
	refreshVendorExpenses: () => void;
	portDAs: GetDisbursementAccountsResponse | undefined;
	refreshDas: () => void;
	vessel: GetVesselDetailsResponse | undefined;
	refreshVessel: () => void;
	loadingVesselDetails: boolean;
	estimateDetails: GetEstimateDetailsResponse | undefined;
	estimateDetailsLoading: boolean;
	refreshEstimateDetails: () => void;
	refreshPnl: () => void;
	linkTheShip: () => void;
	tcPnl: TcPnl | undefined;
	vcPnl: VcPnl | undefined;
	fixtureType: Values<typeof FixtureTypes> | undefined;
	showTcTermsModal: boolean;
	setShowTcTermsModal: (visible: boolean) => void;
	hasCargoRelet: boolean;
};

// Type for useVoyageData return - same as context but with nullable guaranteed fields
// We can guarantee the values are present when using useVoyage, but not when using useVoyageData
// (Which is outside the context)
type VoyageDataReturn = MakeFieldsNullable<VoyageContextType, 'voyageDetails' | 'fixtureDetails'>;

// Hook that fetches all voyage data - can be used standalone or within provider
export const useVoyageData = (voyageId?: number): VoyageDataReturn => {
	const [showTcTermsModal, setShowTcTermsModal] = useState(false);
	const queryClient = useQueryClient();
	
	const {
		data: voyageDetails,
		isLoading: voyageDetailsLoading,
		refetch: refreshVoyageDetails,
	} = useQuery(
		['voyageDetails', voyageId],
		() => getVoyageDetails(voyageId!),
		{
			enabled: voyageId != null,
			refetchOnMount: true,
		},
	);

	const {
		data: voyageCompletion,
		refetch: refreshVoyageCompletion,
	} = useQuery(
		['voyageCompletion', voyageDetails?.id],
		() => {
			if (voyageDetails?.id != null) {
				return getVoyageCompletionType(voyageDetails.id);
			}

			return undefined;
		},
		{
			enabled: voyageDetails != null,
			retry: false,
		},
	);

	const {
		data: allVoyages,
	} = useQuery(
		['allVoyages'],
		() => getVoyages({
			vesselId: voyageDetails?.vesselId!,
			includeCompleted: true,
			disablePagination: true,
		}),
		{
			refetchOnWindowFocus: false,
			retry: false,
			enabled: voyageDetails != null && voyageDetails.vesselId != null
		},
	);

	const {
		data: tcPnl,
		refetch: refreshPnl,
	} = useQuery(
		['tcPnl', voyageId],
		() => getTcPnl(voyageDetails!.id),
		{
			refetchOnWindowFocus: false,
			enabled:
				voyageDetails != null &&
				voyageDetails?.fixture?.type !== FixtureTypes.SPOT &&
				voyageDetails?.id != null,
		},
	);

	const {
		data: vcPnl,
		refetch: refetchPnl,
	} = useQuery(
		['vcPnl', voyageDetails?.id],
		() => getVcPnl(voyageDetails!.id),
		{
			enabled:
				voyageDetails != null &&
				voyageDetails?.id != null &&
				voyageDetails.fixture.type === FixtureTypes.SPOT
		},
	);

	const {
		data: fixtureDetails,
		isLoading: fixtureDetailsLoading,
		refetch: refreshFixtureDetails,
	} = useQuery(
		['fixtureDetails', voyageDetails?.fixtureId],
		() => getFixtureDetails(voyageDetails?.fixtureId!),
		{
			enabled: !!voyageDetails?.fixtureId,
			refetchOnWindowFocus: false,
			retry: false,
		},
	);

	const fixtureType = fixtureDetails?.type;

	const {
		data: voyageInvoices,
		isFetching: voyageInvoicesLoading,
		refetch: refreshVoyageInvoices,
	} = useQuery(
		['voyageInvoices', voyageDetails?.id],
		() => getVoyageInvoices(voyageDetails?.id!),
		{
			enabled: !!voyageDetails?.id && fixtureType != null && fixtureType === FixtureTypes.SPOT,
			refetchOnWindowFocus: false,
			retry: false,
		},
	);

	const {
		data: cargos,
		isFetching: cargosLoading,
		refetch: refreshCargos,
	} = useQuery(
		['cargos', voyageDetails?.id],
		() => getCargosAndLaytimes({
			voyageId: voyageDetails!.id,
		}),
		{
			enabled: (
				fixtureType != null &&
				fixtureType === FixtureTypes.SPOT &&
				voyageDetails?.id != null
			),
			refetchOnWindowFocus: false,
			retry: false,
		},
	);

	const {
		data: vendorExpenses,
		isFetching: vendorExpensesLoading,
		refetch: refreshVendorExpenses,
	} = useQuery(
		['vendorExpenses', voyageDetails?.id],
		() => getVoyageExpenses({
			voyageId: voyageDetails?.id!,
			hideCompleted: false,
		}),
		{
			enabled: !!voyageDetails?.id && fixtureType != null,
			refetchOnWindowFocus: false,
			retry: false,
		},
	);

	const {
		data: portDAs,
		refetch: refreshDas,
	} = useQuery(
		['portDAs', voyageDetails?.id],
		() => getDisbursementAccounts(voyageDetails?.id!),
		{
			enabled: !!voyageDetails?.id,
		}
	);

	const vcFixtureDetails = fixtureDetails as GetFixtureDetailsResponse<SpotFixtureProps>;
	const estimateId = voyageDetails?.estimateId ?? vcFixtureDetails?.estimateId;

	const {
		data: estimateDetails,
		isFetching: estimateDetailsLoading,
		refetch: refreshEstimateDetails,
	} = useQuery(
		['estimateDetails', estimateId],
		() => getEstimateDetails(estimateId!),
		{
			enabled: (
				!!voyageDetails?.id &&
				estimateId != null &&
				fixtureType != null &&
				(
					fixtureType === FixtureTypes.SPOT ||
					fixtureType === FixtureTypes.TC_OUT ||
					fixtureType === FixtureTypes.TC_IN
				) &&
				vcFixtureDetails.estimateId != null
			),
			refetchOnWindowFocus: false,
			retry: false,
		},
	);

	const {
		data: vessel,
		refetch: refreshVessel,
		isFetching: loadingVesselDetails,
	} = useQuery(
		['vessel', voyageDetails?.vesselId],
		() => getVesselDetails(voyageDetails?.vesselId!),
		{
			enabled: !!voyageDetails?.vesselId,
			refetchOnWindowFocus: false,
			retry: false,
		},
	);

	const hasCargoRelet = useMemo(
		() => cargos?.cargos?.some(
			(cargo) => cargo.contractSide === CargoContractSides.VC_IN
		) ?? false,
		[cargos]
	);

	const updateVoyageField = async (field: string, value: any, contractId?: number | null) => {
		const id = contractId ?? voyageDetails?.id;

		if (id == null) {
			return;
		}

		try {
			await updateVoyage(id, { [field]: value });
			await refreshVoyageDetails();
		} catch (e) {
			if (value !== undefined) {
				showErrorNotification('Could not update contract', e as Error);
			}
		}
	};

	const refreshDetails = async () => {
		await Promise.all([
			refreshVoyageDetails(),
			refreshFixtureDetails(),
			refreshVoyageCompletion(),
			refreshPnl(),
			refetchPnl(),
			refreshVendorExpenses(),
			...(fixtureType === FixtureTypes.SPOT ? [
				refreshCargos(),
				refreshVoyageInvoices(),
				voyageDetails?.id != null ?
					queryClient.invalidateQueries(['voyageInvoicesWithDeleted', voyageDetails.id]) :
					Promise.resolve(),
			] : []),
		]);
	};

	const linkTheShip = async () => {
		if (voyageDetails == null) {
			return;
		}

		try {
			await syncVoyageToTheShip(voyageDetails.id);
			await refreshVoyageDetails();
			showSuccessNotification('Successfully linked contract to The Ship');
		} catch (e) {
			showErrorNotification('Could not link contract to The Ship', e as Error);
		}
	};

	const fixtureCurrency = voyageDetails?.bankAccount?.currency ?? Currencies.USD;

	return {
		voyageId: voyageId!,
		voyageDetails,
		voyageDetailsLoading,
		refreshVoyageDetails,
		fixtureCurrency,
		tcFixtureDetails: fixtureDetails as GetFixtureDetailsResponse<TcFixtureProps | BbFixtureProps>,
		vcFixtureDetails,
		fixtureDetails,
		fixtureDetailsLoading,
		refreshFixtureDetails,
		refreshDetails,
		allVoyages: allVoyages?.rows ?? [],
		updateVoyageField,
		voyageCompletion,
		voyageInvoices,
		voyageInvoicesLoading,
		refreshVoyageInvoices,
		cargos: cargos?.cargosAndLaytimes ?? [],
		transformedCargos: cargos?.cargos ?? [],
		cargosLoading,
		refreshCargos,
		vendorExpenses,
		vendorExpensesLoading,
		refreshVendorExpenses,
		portDAs,
		refreshDas,
		vessel,
		refreshVessel,
		loadingVesselDetails,
		estimateDetails,
		estimateDetailsLoading,
		refreshEstimateDetails,
		refreshPnl,
		linkTheShip,
		tcPnl,
		vcPnl,
		fixtureType,
		showTcTermsModal,
		setShowTcTermsModal,
		hasCargoRelet,
	};
};

export const VoyageProvider = ({
	voyageId,
	children,
}: {
	voyageId: number;
	children: ReactNode;
}) => {
	const voyageData = useVoyageData(voyageId);

	if (!voyageData || voyageData.fixtureDetailsLoading || voyageData.fixtureDetails == null || voyageData.voyageDetails == null) {
		return (<ContractLoading />);
	}

	const contextValue: VoyageContextType = voyageData as VoyageContextType;

	return (
		<VoyageContext.Provider value={contextValue}>
			{children}
		</VoyageContext.Provider>
	);
};

const VoyageContext = createContext<VoyageContextType | undefined>(undefined);

export const useVoyage = () => {
	const context = useContext(VoyageContext);

	if (context === undefined) {
		throw new Error('useVoyage must be used within a VoyageProvider');
	}

	return context;
};
